import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  TextField,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  HourglassEmpty as HourglassEmptyIcon,
  Autorenew as AutorenewIcon,
  DoneAll as DoneAllIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useTheme } from "@mui/material/styles";
import axiosInstance from "../../../../../lib/axios";
import TooltipCellContent from "../../../../../GlobalFunction/TooltipCellContent";

const columns = [
  { id: "TktDate", label: "Date", minWidth: { xs: 60, sm: 80 } },
  { id: "TicketNo", label: "Tkt No", minWidth: { xs: 60, sm: 100 } },
  { id: "ServiceName", label: "Service/Complain", minWidth: { xs: 70, sm: 120 } },
  { id: "TicketServName", label: "Severity", minWidth: { xs: 60, sm: 80 } },
  { id: "TicketTagName", label: "Tag", minWidth: { xs: 60, sm: 80 } },
  { id: "TicketTypeName", label: "Type", minWidth: { xs: 60, sm: 80 } },
  { id: "LeadTime", label: "LeadTime", minWidth: { xs: 60, sm: 80 } },
  { id: "CategoryName", label: "Category", minWidth: { xs: 60, sm: 100 } },
  { id: "SubCategoryName", label: "SubCategory", minWidth: { xs: 60, sm: 100 } },
  { id: "PropName", label: "Property", minWidth: { xs: 80, sm: 120 } },
  { id: "MobileNo", label: "Mobile No", minWidth: { xs: 60, sm: 100 } }, // Fixed: was sm2
  { id: "TicketSt", label: "Status", minWidth: { xs: 20, sm: 80 } },
];

const getStatusText = (status) => {
  switch (status) {
    case 'O': return 'Open';
    case 'I': return 'In Progress';
    case 'R': return 'Resolved';
    case 'C': return 'Closed';
    default: return 'Unknown';
  }
};

const TicketDialog = ({
  open,
  onClose,
  globalProperty,
  selectedCategory,
  selectedWarden,
  fromDate,
  toDate,
}) => {
  const theme = useTheme();
  const [rows, setRows] = useState([]);
  const [searchTerms, setSearchTerms] = useState({});

  // Refetch when dialog opens or filters change
  useEffect(() => {
    if (open && globalProperty && selectedCategory?.Id) {
      fetchTickets();
    }
  }, [open, globalProperty, selectedCategory, selectedWarden, fromDate, toDate]);

  const handleSearchChange = (columnId, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [columnId]: value,
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const fetchTickets = async () => {
    if (!globalProperty || !selectedCategory?.Id) {
      setRows([]);
      return;
    }

    try {
      const response = await axiosInstance.post(
        "TrnTicketing/GetFilterTicketDashBoard",
        {
          PageNumber: 1,
          PageSize: 100, // Show up to 100 tickets
          PropId: globalProperty,
          Id: selectedCategory.Id,
          WardenId: selectedWarden?.Id || 1,
          ServiceProvId: 1,
          SearchText: "", // No status filter → show ALL tickets
          SearchFrom: fromDate,
          SearchTo: toDate,
          Flag: "TicketDash",
        }
      );

      const { data: { Status, Data } } = response;

      if (Status === 0 && Array.isArray(Data)) {
        setRows(Data);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching all tickets:", error);
      setRows([]);
    }
  };

  const filteredRows = React.useMemo(() => {
    return rows
      .map((row) => {
        return Object.fromEntries(
          Object.entries(row).map(([key, value]) => [
            key,
            typeof value === "string" ? value.trim() : value,
          ])
        );
      })
      .filter((row) => {
        return Object.entries(searchTerms).every(([columnId, term]) => {
          if (!term) return true;
          const value = row[columnId];
          if (typeof value === "string") {
            return value.toLowerCase().includes(term.toLowerCase());
          } else if (typeof value === "number") {
            return value.toString().includes(term);
          }
          return true;
        });
      });
  }, [searchTerms, rows]);

  const getStatusChip = (status) => {
    let color, icon, bgColor;
    switch (status) {
      case 'O':
        color = 'error';
        icon = <HourglassEmptyIcon fontSize='small' />;
        bgColor = theme.palette.error.main;
        break;
      case 'I':
        color = 'warning';
        icon = <AutorenewIcon fontSize='small' />;
        bgColor = '#FF9800';
        break;
      case 'R':
        color = 'default';
        icon = <DoneAllIcon fontSize='small' />;
        bgColor = '#FF5722';
        break;
      case 'C':
        color = 'success';
        icon = <CancelIcon fontSize='small' />;
        bgColor = '#4CAF50';
        break;
      default:
        color = 'default';
        icon = null;
        bgColor = theme.palette.grey[200];
    }
    return (
      <Chip
        icon={icon}
        label={getStatusText(status)}
        color={color}
        size='small'
        sx={{
          fontWeight: 600,
          borderRadius: '12px',
          backgroundColor: bgColor,
          color: theme.palette.getContrastText(bgColor),
          '& .MuiChip-icon': {
            color: theme.palette.getContrastText(bgColor),
            fontSize: '0.8rem',
          },
          maxWidth: { xs: '80px', sm: '90px', md: '110px' },
          fontSize: { xs: '0.7rem', sm: '0.75rem' },
          padding: { xs: '2px 4px', sm: '4px 6px' },
        }}
      />
    );
  };

  const getRowStyle = (ticketStatus) => {
    switch (ticketStatus) {
      case "C": return { backgroundColor: "#c8e6c9" }; // Closed
      case "O": return { backgroundColor: "#ffe0b2" }; // Open
      case "R": return { backgroundColor: "#ffebee" }; // Resolved
      case "I": return { backgroundColor: "#fff3e0" }; // In Progress
      default: return {};
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          width: { xs: "95%", sm: "95%", md: "95%" },
          maxWidth: { xs: "100%", sm: "lg" },
          m: { xs: 1, sm: 2 },
          boxSizing: 'border-box',
          overflowX: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 2, sm: 3 },
          py: { xs: 1, sm: 2 },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            flexGrow: 1,
            fontWeight: 600,
            fontSize: { xs: "1rem", sm: "1.25rem" },
          }}
        >
          Total Tickets
        </Typography>
        <IconButton edge="end" onClick={onClose}>
          <CloseIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Paper sx={{ width: "100%" }}>
          <TableContainer
            sx={{
              maxHeight: { xs: "60vh", sm: "70vh" },
              width: "100%",
              overflowX: "auto",
              '&::-webkit-scrollbar': {
                height: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'grey.400',
                borderRadius: '4px',
              },
            }}
          >
            <Table stickyHeader sx={{ minWidth: 650, tableLayout: "auto" }}>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        minWidth: column.minWidth,
                        p: { xs: "4px 8px", sm: "4px 12px" },
                        backgroundColor: "grey.100",
                      }}
                    >
                      <Box display="flex" flexDirection="column">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                          }}
                        >
                          {column.label}
                        </Typography>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder={`Search ${column.label}`}
                          onChange={(e) => handleSearchChange(column.id, e.target.value)}
                          sx={{
                            mt: 1,
                            width: "100%",
                            backgroundColor: "#e0f7fa",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: { xs: "0.7rem", sm: "0.75rem" },
                              height: { xs: "24px", sm: "26px" },
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((row) => (
                    <TableRow
                      key={row.TicketId}
                      sx={{
                        ...getRowStyle(row.TicketSt),
                        "&:hover": { backgroundColor: "grey.200" },
                      }}
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          sx={{
                            p:
                              column.id === "TktDate" || column.id === "TicketSt"
                                ? { xs: "6px 8px", sm: "8px 16px" }
                                : { xs: "4px 6px", sm: "4px 8px" },
                            fontSize: { xs: "0.7rem", sm: "0.875rem" },
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: 150,
                          }}
                        >
                          {column.id === "TktDate"
                            ? formatDate(row[column.id])
                            : column.id === "TicketSt" ? (
                              <TooltipCellContent title={getStatusText(row[column.id])}>
                                {getStatusChip(row[column.id])}
                              </TooltipCellContent>
                            ) : (
                              <TooltipCellContent title={row[column.id] || '—'}>
                                {row[column.id] || '—'}
                              </TooltipCellContent>
                            )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2">
                        No tickets found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </DialogContent>
      <DialogActions />
    </Dialog>
  );
};

export default TicketDialog;